<template>
  <WrappedForm
    v-if="show"
    ref="form"
    class="job-additional-info"
    label-width="200px"
    :model="stepData"
    :formLines="formLines"
    :disabled="disabled"
  >
    <template #emailEditor="{ lineName, line }">
      <EmailEditor :ref="`emailEditor_${lineName}`" v-model="stepData[lineName]" :disabled="disabled" :maxSize="line.maxSize" :tipMessage="line.tipMessage"></EmailEditor>
    </template>
  </WrappedForm>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import moment from 'moment'
import mixin from './mixin'
import EmailEditor from './emailEditor.vue'
import { addI18nProperty } from '@/i18n'
import { formatDate } from '@/components/filters'
import { CUSTOM_FIELD_TYPE } from '@/constant/job'

export default {
  mixins: [mixin],
  props: {
    company: {
      type: Object,
      default: null
    }
  },
  components: {
    EmailEditor
  },
  data () {
    return {
      jobTagList: [],
      jobTagListLoader: null,
      filedCompanyId: null,
      customFieldLoader: null,
      customFieldList: []
    }
  },
  computed: {
    hasReceiveResumeEmailPermission () {
      return this.company?.permission?.receive_resume_email
    },
    formLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const requiredRule = [{ required: true, message: requiredMessage, trigger: 'blur' }]
      const customFieldLines = {}
      _.forEach(this.customFieldList, customField => {
        const fieldType = customField.type
        const lineName = `customField_${customField.id}`
        const lineProps = { clearable: true }
        const line = {
          elType: 'el-input',
          props: lineProps,
          rules: null
        }
        addI18nProperty(line, 'label', 'cn_name', 'name', customField)
        if (fieldType === CUSTOM_FIELD_TYPE.SELECT) {
          line.elType = 'el-select'
          lineProps.filterable = true
          lineProps.multiple = true
          line.option = {
            data: customField.options,
            valueKey: 'id',
            labelKey: 'i18nName'
          }
        } else if (fieldType === CUSTOM_FIELD_TYPE.SINGLE) {
          line.elType = 'el-select'
          lineProps.filterable = true
          lineProps.multiple = false
          line.option = {
            data: customField.options,
            valueKey: 'id',
            labelKey: 'i18nName'
          }
        } else if (fieldType === CUSTOM_FIELD_TYPE.TEXT) {

        } else if (fieldType === CUSTOM_FIELD_TYPE.TEXTAREA) {
          lineProps.type = 'textarea'
          lineProps.autosize = { minRows: 3 }
        } else if (fieldType === CUSTOM_FIELD_TYPE.DATE) {
          line.elType = 'el-date-picker'
        }
        if (customField.is_required) {
          lineProps.clearable = false
          line.rules = requiredRule
        }
        customFieldLines[lineName] = line
      })
      const lines = {
        reminderEmail: {
          label: this.$t(`${this.i18nPrefix}.label.reminderEmail`),
          contentSlot: 'emailEditor',
          tipMessage: this.$t(`${this.i18nPrefix}.message.addNewEmailTip.reminder`)
        },
        resumeEmail: {
          hidden: !this.hasReceiveResumeEmailPermission,
          label: this.$t(`${this.i18nPrefix}.label.resumeEmail`),
          contentSlot: 'emailEditor',
          maxSize: 3,
          tipMessage: this.$t(`${this.i18nPrefix}.message.addNewEmailTip.resume`)
        },
        ...customFieldLines
      }
      return lines
    }
  },
  created () {
    this.loadCustomField()
  },
  methods: {
    ...mapActions('jobCustomField', ['searchJobCustomField']),
    async loadCustomField () {
      if (!this.hasCustomFiledPermission) {
        return
      }
      const companyId = this.companyDataParams?.company_id || this.jobInfo.company?.id
      if (this.filedCompanyId !== companyId || !this.customFieldLoader) {
        this.filedCompanyId = companyId
        const params = {
          ...(this.companyDataParams || {}),
          company_id: companyId,
          need_options: true,
          status: 1
        }
        if (this.isEditMode) {
          params.job_id = this.jobInfo.id
        }
        this.customFieldLoader = this.searchJobCustomField(params).then(resp => {
          if (resp) {
            this.customFieldList = _.map(resp, customField => {
              _.forEach(customField.options, optionInfo => addI18nProperty(optionInfo, 'i18nName', 'cn_name', 'name'))
              return customField
            })
          }
          return this.customFieldList
        })
      }
      await this.customFieldLoader
      return this.customFieldList
    },
    setStepDataToJobInfo () {
      const stepData = this.stepData
      const customFields = []
      _.forEach(this.customFieldList, customField => {
        const dataKey = `customField_${customField.id}`
        const fieldValue = stepData[dataKey]
        const fieldType = customField.type
        const boundFieldInfo = {
          id: customField.id,
          type: fieldType,
          filter_value: null,
          filter_value_items: null
        }
        if (fieldType === CUSTOM_FIELD_TYPE.SELECT) {
          const options = customField.options
          boundFieldInfo.filter_value_items = _.map(fieldValue, value => {
            const option = _.find(options, { id: value })
            return option
          })
        } else if (fieldType === CUSTOM_FIELD_TYPE.SINGLE) {
          boundFieldInfo.filter_value_items = [{ id: fieldValue }]
        } else if (fieldType === CUSTOM_FIELD_TYPE.DATE) {
          boundFieldInfo.filter_value = formatDate(fieldValue, null)
        } else {
          boundFieldInfo.filter_value = fieldValue
        }
        customFields.push(boundFieldInfo)
      })
      const jobInfo = this.jobInfo
      this.$set(jobInfo, 'custom_fields', customFields)
      this.$set(jobInfo, 'notification_emails', stepData.reminderEmail)
      this.$set(jobInfo, 'forward_emails', stepData.resumeEmail)
    },
    async getStepData () {
      await this.loadCustomField()
      const jobInfo = this.jobInfo
      const stepData = {
        reminderEmail: jobInfo.notification_emails || [],
        resumeEmail: jobInfo.forward_emails || []
      }
      _.forEach(this.customFieldList, customField => {
        const dataKey = `customField_${customField.id}`
        stepData[dataKey] = null
      })
      _.forEach(jobInfo.custom_fields, boundFieldInfo => {
        const fieldType = boundFieldInfo.type
        let fieldValue = null
        if (fieldType === CUSTOM_FIELD_TYPE.SELECT) {
          fieldValue = _.map(boundFieldInfo.filter_value_items, 'id')
        } else if (fieldType === CUSTOM_FIELD_TYPE.SINGLE) {
          fieldValue = null
          if (boundFieldInfo.filter_value_items.length) {
            fieldValue = boundFieldInfo.filter_value_items[0].id
          }
        } else if (fieldType === CUSTOM_FIELD_TYPE.DATE) {
          fieldValue = moment(boundFieldInfo.filter_value).valueOf()
        } else {
          fieldValue = boundFieldInfo.filter_value
        }
        const dataKey = `customField_${boundFieldInfo.id}`
        stepData[dataKey] = fieldValue
      })
      return stepData
    }
  }
}
</script>

<style lang="scss" scoped>
.job-additional-info {
  .email-editor {
    line-height: normal;
  }
}
</style>
